import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Cpu, Gamepad2, Loader2, X } from "lucide-react";
import { API } from "@/lib/constants";
import { getStoredReferralCode } from "@/lib/referral";

const plans = [
  {
    id: "console",
    icon: Gamepad2,
    name: "Console",
    price: 5,
    tagline: "PS5 / Xbox / Switch",
    features: [
      "Network & DNS tuning",
      "Reduced input lag",
      "Optimal display settings",
      "Cooling & temp guidance",
      "1-on-1 Discord walkthrough",
    ],
  },
  {
    id: "pc",
    icon: Cpu,
    name: "PC",
    price: 15,
    tagline: "Windows 10 / 11",
    highlight: true,
    features: [
      "Full Windows debloat",
      "GPU & CPU optimization",
      "Latency & jitter reduction",
      "FPS-tuned game configs",
      "1-on-1 Discord walkthrough",
    ],
  },
];

// PC is now bought and unlocked inside the desktop app itself — the website's job there is just to
// get someone to download it. Console-only stays exactly as it was: paid here, claimed via a
// Discord ticket, since there's no app running on a PS5/Xbox to unlock anything. (The PC+Console
// combo plan has been discontinued — existing customers who bought it keep their unlock, it's just
// no longer offered.)
const APP_PLAN_IDS = new Set(["pc"]);

export const Pricing = () => {
  // Console is the only plan bought here — pc's "buy" button just points at the app download
  // (see APP_PLAN_IDS below), and the app's own checkout call doesn't send a referral code today.
  const [referralCode, setReferralCode] = useState("");
  const [validation, setValidation] = useState("idle"); // idle | checking | valid | invalid
  // Plan id whose checkout session is currently being created, or null. Creating the session is a
  // round trip to Stripe and can take several seconds on a cold serverless function, during which
  // the button previously looked completely inert - people assumed checkout was broken and either
  // gave up or clicked again, minting a second session. This drives both the label and the guard.
  const [pending, setPending] = useState(null);
  // Checkout failures used to fire a browser alert(), which on mobile is a modal you have to
  // dismiss before you can even see the page again. Shown inline under the buttons instead.
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    const stored = getStoredReferralCode();
    if (stored) setReferralCode(stored);
  }, []);

  useEffect(() => {
    const code = referralCode.trim();
    if (!code) {
      setValidation("idle");
      return;
    }
    setValidation("checking");
    const timeout = setTimeout(() => {
      fetch(`${API}/referral/validate/${encodeURIComponent(code.toUpperCase())}`)
        .then((r) => r.json())
        .then((data) => setValidation(data.valid ? "valid" : "invalid"))
        .catch(() => setValidation("idle"));
    }, 400);
    return () => clearTimeout(timeout);
  }, [referralCode]);

  const handlePay = async (serviceId) => {
    if (pending) return;
    setCheckoutError(null);
    setPending(serviceId);
    try {
      const trimmedCode = referralCode.trim();
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // A bad/unknown code is validated (and silently dropped) server-side too — this client
        // check just avoids sending obvious junk, it isn't what enforces validity.
        body: JSON.stringify({ service: serviceId, ...(trimmedCode ? { referral_code: trimmedCode } : {}) }),
      });
      const data = await res.json();
      if (data.url) {
        // Deliberately leave pending set: the browser is navigating away to Stripe, and clearing it
        // would flash the button back to "Pay $5" in the moment before the page unloads.
        window.location.href = data.url;
        return;
      }
      setPending(null);
      setCheckoutError("Stripe did not return a checkout link. Try again, or message us on Discord and we will take the order manually.");
    } catch (err) {
      setPending(null);
      setCheckoutError("Could not reach checkout — check your connection and try again. If it keeps failing, message us on Discord.");
    }
  };

  const scrollToDownload = () => {
    document.getElementById("download")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" className="relative py-24 sm:py-32 overflow-hidden" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-14"
        >
          <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4 slash">
            Pricing
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Cheaper than <span className="text-orange-500">a single skin.</span>
            <br />
            <span className="text-zinc-500">Worth way more.</span>
          </h2>
        </motion.div>

        <div className="max-w-3xl mx-auto mb-6">
          <label className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">
            Referral Code <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
          </label>
          <div className="relative max-w-xs">
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="e.g. AB12CD"
              maxLength={8}
              className="w-full bg-black border border-white/10 focus:border-orange-500 focus:outline-none rounded-none px-3 py-2.5 text-sm tracking-widest uppercase placeholder:text-zinc-700 placeholder:tracking-normal placeholder:normal-case"
              data-testid="pricing-referral-input"
            />
            {validation === "checking" && (
              <Loader2 className="w-4 h-4 text-zinc-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
            )}
            {validation === "valid" && (
              <Check className="w-4 h-4 text-orange-500 absolute right-3 top-1/2 -translate-y-1/2" />
            )}
            {validation === "invalid" && (
              <X className="w-4 h-4 text-red-500 absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>
          {/* The icon alone left people guessing. Note that an unrecognised code never blocks the
              sale — the server drops it silently — so the invalid message says so outright. */}
          <p className="mt-2 text-[11px] min-h-[16px]" aria-live="polite">
            {validation === "valid" && (
              <span className="text-orange-500">Code accepted — your referrer gets credited.</span>
            )}
            {validation === "invalid" && (
              <span className="text-red-400">
                We don&apos;t recognise that code. You can still buy — it just won&apos;t credit anyone.
              </span>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch max-w-3xl mx-auto">
          {plans.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`relative flex flex-col bg-[#080808] border p-8 sm:p-10 transition-all duration-300 ${
                  p.highlight
                    ? "border-orange-500 md:-translate-y-4 shadow-[0_0_50px_rgba(255,94,0,0.18)] hover:shadow-[0_0_70px_rgba(255,94,0,0.28)]"
                    : "border-white/10 hover:border-orange-500/40 hover:bg-[#0c0c0c]"
                }`}
                data-testid={`pricing-card-${p.id}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-8 px-3 py-1 bg-orange-500 text-black text-[10px] font-black tracking-[0.2em] uppercase">
                    Most Popular
                  </div>
                )}

                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 border border-white/10 flex items-center justify-center bg-black">
                    <Icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500">
                    {p.tagline}
                  </span>
                </div>

                <h3
                  className="text-2xl font-black uppercase tracking-tight"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  {p.name}
                </h3>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-5xl sm:text-6xl font-black text-white tabular-nums leading-none tracking-tighter">
                    ${p.price}
                  </span>
                  <span className="text-zinc-500 text-sm uppercase tracking-widest">one-time</span>
                </div>

                <div className="mt-8 h-px w-full bg-white/5" />

                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {APP_PLAN_IDS.has(p.id) ? (
                  <>
                    <button
                      onClick={scrollToDownload}
                      className={`mt-8 w-full py-4 font-black uppercase tracking-[0.2em] text-xs transition-all text-center ${
                        p.highlight
                          ? "bg-orange-500 text-black hover:bg-orange-400 hover:shadow-[0_0_30px_rgba(255,94,0,0.5)]"
                          : "bg-white/5 text-white hover:bg-orange-500 hover:text-black"
                      }`}
                      data-testid={`pricing-cta-${p.id}`}
                    >
                      Get The App
                    </button>
                    <p className="mt-3 text-[10px] text-center text-zinc-500 uppercase tracking-wide">
                      Purchased &amp; unlocked inside the app
                    </p>
                  </>
                ) : (
                  <button
                    onClick={() => handlePay(p.id)}
                    disabled={pending !== null}
                    className={`mt-8 w-full py-4 font-black uppercase tracking-[0.2em] text-xs transition-all text-center disabled:cursor-not-allowed ${
                      p.highlight
                        ? "bg-orange-500 text-black hover:bg-orange-400 hover:shadow-[0_0_30px_rgba(255,94,0,0.5)]"
                        : "bg-white/5 text-white hover:bg-orange-500 hover:text-black"
                    } ${pending !== null ? "opacity-60" : ""}`}
                    data-testid={`pricing-cta-${p.id}`}
                  >
                    {pending === p.id ? "Opening Stripe..." : `Pay $${p.price}`}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          {checkoutError && (
            <p
              className="max-w-md border border-red-500/30 bg-red-500/[0.06] px-4 py-3 text-center text-xs leading-relaxed text-red-300"
              role="alert"
              data-testid="pricing-checkout-error"
            >
              {checkoutError}
            </p>
          )}
          <p className="text-xs tracking-[0.25em] uppercase text-zinc-500 text-center">
            No Subscriptions · No Bloatware · Secure Checkout via Stripe
          </p>
        </div>
      </div>
    </section>
  );
};
